<template>
  <!-- `table-wrapper` gives the table the theme's dark rows, as elsewhere. -->
  <el-form
    :disabled="disabled"
    class="column-mappings table-wrapper"
    @submit.native.prevent
  >
    <el-table
      :data="rows"
      row-key="key"
    >
      <el-empty
        slot="empty"
        :description="$t(`${I18N}.empty`)"
        :image-size="80"
      />

      <el-table-column
        :label="$t(`${I18N}.sourceColumn`)"
        min-width="200"
      >
        <template slot-scope="{ row }">
          <FileLabel :label="row.sourceColumn" />
          <div class="column-mappings__state">
            <el-tag
              v-if="isUnmapped(row)"
              type="warning"
              size="mini"
            >
              <i
                class="el-icon-warning-outline"
                aria-hidden="true"
              />
              {{ $t(`${I18N}.unmapped`) }}
            </el-tag>
            <el-tag
              v-else-if="row.ignored"
              type="info"
              size="mini"
            >
              {{ $t(`${I18N}.ignored`) }}
            </el-tag>
            <span
              v-if="isPending(row)"
              class="column-mappings__secondary column-mappings__pending"
            >
              {{ $t(`${I18N}.pending`) }}
            </span>
          </div>
          <!-- The failed write of the row: said once, under the column. -->
          <div
            v-if="serverErrors[row.key]"
            class="column-mappings__error"
            role="alert"
          >
            <i
              class="el-icon-warning-outline"
              aria-hidden="true"
            />
            {{ serverErrors[row.key] }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.analysis.label`)"
        min-width="300"
      >
        <template slot-scope="{ row }">
          <span
            v-if="row.ignored"
            class="column-mappings__secondary"
          >
            {{ $t(`${I18N}.ignoredCells`) }}
          </span>
          <el-form-item
            v-else
            :error="fieldError(row, 'analysisId')"
            :show-message="false"
            :title="fieldError(row, 'analysisId')"
          >
            <MappingAnalysisSelect
              :value="row.analysisId"
              :analysis="row.analysis"
              @change="change(row, $event)"
            />
          </el-form-item>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.ldThreshold.label`)"
        width="140"
      >
        <el-form-item
          v-if="!row.ignored"
          slot-scope="{ row }"
          :error="fieldError(row, 'ldThreshold')"
          :show-message="false"
          :title="fieldError(row, 'ldThreshold')"
        >
          <el-input
            :value="row.ldThreshold"
            size="small"
            :placeholder="$t(`${I18N}.ldThreshold.placeholder`)"
            :aria-label="$t(`${I18N}.ldThreshold.label`)"
            @input="change(row, { ldThreshold: $event })"
          />
        </el-form-item>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.decimals.label`)"
        width="140"
      >
        <!-- `change`, not `input`: el-input-number emits `input` on mount. -->
        <el-form-item
          v-if="!row.ignored"
          slot-scope="{ row }"
          :error="fieldError(row, 'decimals')"
          :show-message="false"
          :title="fieldError(row, 'decimals')"
        >
          <el-input-number
            :value="row.decimals"
            :min="0"
            :max="MAX_DECIMALS"
            step-strictly
            controls-position="right"
            size="small"
            :aria-label="$t(`${I18N}.decimals.label`)"
            @change="change(row, { decimals: $event })"
          />
        </el-form-item>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.measure.label`)"
        min-width="160"
      >
        <el-form-item
          v-if="!row.ignored"
          slot-scope="{ row }"
          :error="fieldError(row, 'measureId')"
          :show-message="false"
          :title="fieldError(row, 'measureId')"
        >
          <MeasureSelect
            :value="row.measureId"
            :measure="row.measure"
            @change="change(row, $event)"
          />
        </el-form-item>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.actions.label`)"
        width="280"
      >
        <!-- One gap for every action: el-popconfirm wraps its button, which
             Element's `.el-button + .el-button` margin does not reach. -->
        <div
          slot-scope="{ row }"
          class="column-mappings__actions"
        >
          <template v-for="action in actionsOf(row)">
            <el-popconfirm
              v-if="confirmTextKey(action, row)"
              :key="action"
              :title="$t(confirmTextKey(action, row))"
              :confirm-button-text="$t(`${I18N}.actions.${action}`)"
              :cancel-button-text="$t('global.cancel')"
              @confirm="$emit('action', { action, row })"
            >
              <el-button
                slot="reference"
                type="text"
                size="small"
                :icon="ACTION_ICONS[action]"
              >
                {{ $t(`${I18N}.actions.${action}`) }}
              </el-button>
            </el-popconfirm>
            <el-button
              v-else
              :key="action"
              type="text"
              size="small"
              :icon="ACTION_ICONS[action]"
              @click="$emit('action', { action, row })"
            >
              {{ $t(`${I18N}.actions.${action}`) }}
            </el-button>
          </template>
        </div>
      </el-table-column>
    </el-table>
  </el-form>
</template>

<script>
  import FileLabel from './FileLabel.vue';
  import MappingAnalysisSelect from './MappingAnalysisSelect.vue';
  import MeasureSelect from './MeasureSelect.vue';
  import { MAX_DECIMALS, ROW_ACTIONS, rowActions } from './columnMappings';

  const ACTION_ICONS = Object.freeze({
    [ROW_ACTIONS.DUPLICATE]: 'el-icon-copy-document',
    [ROW_ACTIONS.IGNORE]: 'el-icon-remove-outline',
    [ROW_ACTIONS.MAP]: 'el-icon-connection',
    [ROW_ACTIONS.DEACTIVATE]: 'el-icon-delete',
    [ROW_ACTIONS.REMOVE]: 'el-icon-close',
    [ROW_ACTIONS.UNDO]: 'el-icon-refresh-left',
  });

  // The rows of the columns step (FE-4), as given: it emits every edit and
  // action and keeps no state of its own.
  export default {
    name: 'ColumnMappingsTable',
    components: { FileLabel, MappingAnalysisSelect, MeasureSelect },
    props: {
      rows: { type: Array, required: true },
      columnChanges: { type: Object, required: true },
      // File columns neither mapped nor ignored when the step opened.
      unmappedColumns: { type: Array, required: true },
      // Rows with a write waiting for "Continua".
      pendingKeys: { type: Array, required: true },
      // Messages by row key, then by field.
      fieldErrors: { type: Object, required: true },
      // Message of the last failed write, by row key.
      serverErrors: { type: Object, required: true },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return {
        I18N: 'analysesData.importValues.columns',
        MAX_DECIMALS,
        ACTION_ICONS,
      };
    },
    methods: {
      actionsOf(row) {
        return rowActions(row, this.columnChanges[row.sourceColumn]);
      },
      isUnmapped(row) {
        return (
          !row.ignored &&
          row.id === null &&
          this.unmappedColumns.includes(row.sourceColumn)
        );
      },
      isPending(row) {
        return Boolean(row.pending) || this.pendingKeys.includes(row.key);
      },
      fieldError(row, field) {
        const errors = this.fieldErrors[row.key];
        return (errors && errors[field]) || '';
      },
      // Actions that drop saved state ask first; the others are undone as easily.
      confirmTextKey(action, row) {
        const actions = `${this.I18N}.actions`;
        if (action === ROW_ACTIONS.DEACTIVATE) {
          return row.ignored
            ? `${actions}.confirmDeactivateIgnored`
            : `${actions}.confirmDeactivate`;
        }
        if (
          action === ROW_ACTIONS.IGNORE &&
          !this.unmappedColumns.includes(row.sourceColumn)
        ) {
          return `${actions}.confirmIgnore`;
        }
        return null;
      },
      change(row, changes) {
        this.$emit('change', { row, changes });
      },
    },
  };
</script>

<style lang="scss" scoped>
  // A table cell holds one control: no form spacing.
  .column-mappings .el-form-item {
    margin-bottom: 0;
  }

  .column-mappings__state {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    line-height: 1.6;
  }

  // Unsaved, as the dot of an edited document: quiet, and never colour alone.
  .column-mappings__pending::before {
    content: '';
    display: inline-block;
    width: 0.375rem;
    height: 0.375rem;
    margin-right: 0.375rem;
    border-radius: 50%;
    background-color: $--color-primary;
    vertical-align: middle;
  }

  .column-mappings__actions {
    display: flex;
    align-items: center;
    gap: 1rem;

    .el-button + .el-button {
      margin-left: 0;
    }
  }

  .column-mappings__error {
    margin-top: 0.25rem;
    color: $--color-danger;
    font-size: 0.85em;
    line-height: 1.4;
  }

  .column-mappings__secondary {
    color: $--color-text-regular;
    font-size: 0.85em;
  }
</style>
